/**
 * F-160 D4 — minimal CSS-only tooltip atom.
 *
 * Renders a trigger (an info `i` glyph by default) that surfaces a tooltip
 * bubble on hover, focus, or touch. CSS-driven so there's no portal /
 * positioning library to ship; the bubble is `position: absolute` over
 * the trigger and consumers wrap the trigger inline next to a form
 * label or link.
 *
 * Usage:
 *   <Tooltip content="Лиши порожнім для discrete замовлень" />
 */
import type { ReactNode } from 'react';
import { useId, useState } from 'react';
import styles from './Tooltip.module.css';

export interface TooltipProps {
  content: ReactNode;
  /** Optional override for the trigger glyph; defaults to `i`. */
  trigger?: ReactNode;
  /** Where the bubble appears relative to the trigger. Default: `top`. */
  placement?: 'top' | 'bottom';
  ariaLabel?: string;
}

export function Tooltip({
  content,
  trigger,
  placement = 'top',
  ariaLabel = 'Підказка',
}: TooltipProps): JSX.Element {
  const id = useId();
  // `open` only flips on touch (`:focus` keeps it open after tap). Hover
  // is handled by CSS `:hover` so we don't pay React state for it.
  const [open, setOpen] = useState(false);
  return (
    <span className={styles.root}>
      <button
        type="button"
        className={styles.trigger}
        aria-label={ariaLabel}
        aria-describedby={id}
        onClick={(e) => {
          // Don't let clicks bubble into a parent interactive (e.g. a
          // `<Card interactive>` that opens a modal).
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        onBlur={() => setOpen(false)}
      >
        {trigger ?? 'i'}
      </button>
      <span
        id={id}
        role="tooltip"
        className={[
          styles.bubble,
          placement === 'bottom' ? styles.bottom : styles.top,
          open ? styles.forceOpen : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {content}
      </span>
    </span>
  );
}
